// src/components/dashboard/AdminDashboard.jsx

import React, { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { 
  BarChart3, 
  Clock, 
  Users, 
  Wrench, 
  TrendingUp, 
  Calendar,
  AlertCircle,
  CheckCircle,
  Eye,
  RefreshCw,
  Activity,
  ClipboardList
} from 'lucide-react';
import { resolveDisplayName, buildDisplayNameFromMaybeObject, isLikelyUsername } from '../../lib/displayName';
import SmartAdaptiveList from '../ui/SmartAdaptiveList';
import OrdenDetalleModal from '../ordenes/OrdenDetalleModal';
import HistorialHorasModal from './HistorialHorasModal';
import OrdenesExtrasModal from './OrdenesExtrasModal';

function DashboardStatCard({ icon: Icon, label, value, hint, accent = 'emerald', detail }) {
  const tones = {
    emerald: {
      shell: 'from-emerald-500/[0.14] via-white to-cyan-500/[0.10]',
      iconWrap: 'bg-emerald-500/12 text-emerald-700 ring-emerald-500/10',
      value: 'text-emerald-950',
      hint: 'text-emerald-700',
      detail: 'border-emerald-200/70 bg-emerald-50/80 text-emerald-700'
    },
    cyan: {
      shell: 'from-cyan-500/[0.14] via-white to-sky-500/[0.10]',
      iconWrap: 'bg-cyan-500/12 text-cyan-700 ring-cyan-500/10',
      value: 'text-slate-950',
      hint: 'text-cyan-700',
      detail: 'border-cyan-200/70 bg-cyan-50/80 text-cyan-700'
    },
    amber: {
      shell: 'from-amber-500/[0.14] via-white to-orange-500/[0.10]',
      iconWrap: 'bg-amber-500/12 text-amber-700 ring-amber-500/10',
      value: 'text-slate-950',
      hint: 'text-amber-700',
      detail: 'border-amber-200/70 bg-amber-50/80 text-amber-700'
    },
    rose: {
      shell: 'from-rose-500/[0.14] via-white to-red-500/[0.10]',
      iconWrap: 'bg-rose-500/12 text-rose-700 ring-rose-500/10',
      value: 'text-slate-950',
      hint: 'text-rose-700',
      detail: 'border-rose-200/70 bg-rose-50/80 text-rose-700'
    }
  };

  const palette = tones[accent] || tones.emerald;

  return (
    <article className={`group relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br ${palette.shell} p-5 shadow-[0_22px_45px_-28px_rgba(15,23,42,0.28)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_28px_55px_-28px_rgba(15,23,42,0.34)]`}>
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80" />
      <div className="relative flex items-start justify-between gap-4">
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{label}</p>
          <div>
            <p className={`text-3xl font-semibold tracking-tight ${palette.value}`}>{value}</p>
            <p className={`mt-1 text-sm font-medium ${palette.hint}`}>{hint}</p>
          </div>
          {detail ? (
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${palette.detail}`}>
              {detail}
            </span>
          ) : null}
        </div>

        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${palette.iconWrap} shadow-sm transition-transform duration-200 group-hover:scale-105`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [horas, setHoras] = useState(null);
  const [horasTecnicos, setHorasTecnicos] = useState(null);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordenDetalle, setOrdenDetalle] = useState(null);
  const [showHistorial, setShowHistorial] = useState(false);
  const [techOrdenesModal, setTechOrdenesModal] = useState(null);
  
  // Filtros para horas técnicos
  const [filtroTecnicos, setFiltroTecnicos] = useState({
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear()
  });

  // Función helper para convertir horas decimales a formato HH:MM
  const formatearHoras = (horasDecimal) => {
    if (horasDecimal === null || horasDecimal === undefined || isNaN(horasDecimal)) {
      return '0:00';
    }
    const horas = Math.floor(horasDecimal);
    const minutos = Math.round((horasDecimal - horas) * 60);
    return `${horas}:${minutos.toString().padStart(2, '0')}`;
  };

  const obtenerNombreTecnico = (orden) => {
    if (!orden) return "";

    // Siempre intentar lookup si hay username almacenado
    const candidateValues = [
      buildDisplayNameFromMaybeObject(orden.nombre_tecnico),
      buildDisplayNameFromMaybeObject(orden.tecnico_nombre),
      buildDisplayNameFromMaybeObject(orden.tecnico),
    ].filter(Boolean);

    // Si algún valor parece username, intentar resolverlo desde otros campos
    const usernameCandidate = candidateValues.find((value) =>
      value && isLikelyUsername(value)
    );
    
    if (usernameCandidate) {
      // Intentar obtener nombre completo de campos de objeto
      const fullNameFromObj = buildDisplayNameFromMaybeObject(
        orden.tecnico_asignado || orden.tecnico_responsable || orden.tecnico_detalle
      );
      if (fullNameFromObj && !isLikelyUsername(fullNameFromObj)) {
        return fullNameFromObj;
      }
    }

    // Usar resolución estándar
    return resolveDisplayName({
      primary: orden?.nombre_tecnico,
      secondary: [
        orden?.tecnico_nombre,
        orden?.tecnico,
        orden?.tecnico_detalle,
        orden?.tecnico_asignado,
        orden?.tecnico_asignado_nombre,
        orden?.usuario_creador_nombre,
        orden?.usuario_creador,
      ],
      lookup: () =>
        buildDisplayNameFromMaybeObject(
          orden?.tecnico_asignado || orden?.tecnico_responsable
        ),
    });
  };

  const cargarDatos = () => {
    setError(null);
    Promise.allSettled([
      http.get('/stats/dashboard'),
      http.get('/stats/horas-clientes', { timeout: 10000 }),
      http.get('/ordenes'),
      http.get('/stats/horas-tecnicos', { 
        params: { 
          mes: filtroTecnicos.mes, 
          anio: filtroTecnicos.anio 
        } 
      })
    ]).then(([a, b, c, d]) => {
      if (a.status === 'fulfilled') setStats(a.value.data);
      else setStats(null);
      
      if (b.status === 'fulfilled') {
        const horasData = b.value.data;
        // Calcular totales desde el array de clientes
        if (horasData?.clientes && Array.isArray(horasData.clientes)) {
          const totales = horasData.clientes.reduce((acc, cliente) => {
            acc.total_contratadas += cliente.horas_contratadas || 0;
            acc.total_trabajadas += cliente.horas_trabajadas || 0;
            acc.total_restantes += cliente.horas_restantes || 0;
            acc.total_extras += cliente.horas_extras || 0;
            return acc;
          }, { total_contratadas: 0, total_trabajadas: 0, total_restantes: 0, total_extras: 0 });
          
          setHoras({
            ...horasData,
            ...totales
          });
        } else {
          setHoras(horasData);
        }
      } else {
        setHoras(null);
      }
      
      if (c.status === 'fulfilled') {
        const ord = (Array.isArray(c.value.data) ? c.value.data : [])
          .sort((x, y) => {
            const dateX = new Date(x.created_at || x.fecha_inicio || x.fecha);
            const dateY = new Date(y.created_at || y.fecha_inicio || y.fecha);
            return dateY - dateX;
          })
          .slice(0, 10);
        setOrdenes(ord);
      } else {
        console.error('Error cargando órdenes del dashboard:', c.reason);
        setOrdenes([]);
      }

      if (d && d.status === 'fulfilled') {
        setHorasTecnicos(d.value.data);
      }
      
      if (a.status !== 'fulfilled' || b.status !== 'fulfilled' || c.status !== 'fulfilled') {
        setError('No se pudieron cargar todos los datos del dashboard.');
      }
    }).catch((e) => {
      console.error('Dashboard request error', e);
      setError('Error de conexión con el servidor.');
    }).finally(() => setLoading(false));
  };

  useEffect(cargarDatos, []);

  // Recargar horas técnicos cuando cambian los filtros
  useEffect(() => {
    http.get('/stats/horas-tecnicos', { 
      params: { 
        mes: filtroTecnicos.mes, 
        anio: filtroTecnicos.anio 
      } 
    }).then(res => {
      setHorasTecnicos(res.data);
    }).catch(err => console.error("Error cargando horas técnicos:", err));
  }, [filtroTecnicos]);

  const fmt = (d) => {
    if (!d) return '-';
    try {
      // Intentar varios formatos de fecha
      let date;
      if (d.includes('T')) {
        // ISO format
        date = new Date(d);
      } else if (d.includes('-')) {
        // YYYY-MM-DD format
        date = new Date(d + 'T00:00:00');
      } else {
        // Asumir que es un timestamp o otro formato
        date = new Date(d);
      }
      
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '-';
    }
  };

  const statCards = stats ? [
    {
      label: 'Clientes activos',
      value: stats?.resumen?.total_clientes ?? '-',
      hint: `${stats?.clientes?.mensuales ?? '-'} mensuales y ${stats?.clientes?.ocasionales ?? '-'} ocasionales`,
      detail: 'Base operativa',
      icon: Users,
      accent: 'emerald'
    },
    {
      label: 'Equipos en gestión',
      value: stats?.resumen?.total_equipos ?? '-',
      hint: 'Inventario bajo seguimiento técnico',
      detail: 'Cobertura activa',
      icon: Wrench,
      accent: 'cyan'
    },
    {
      label: 'Órdenes abiertas',
      value: stats?.resumen?.total_ordenes ?? '-',
      hint: `${stats?.ordenes?.pendientes ?? '-'} pendientes y ${stats?.ordenes?.en_proceso ?? '-'} en proceso`,
      detail: 'Carga actual',
      icon: ClipboardList,
      accent: 'amber'
    },
    {
      label: 'Órdenes completadas',
      value: stats?.ordenes?.completadas ?? '-',
      hint: 'Trabajos finalizados y cerrados',
      detail: 'Rendimiento',
      icon: CheckCircle,
      accent: 'rose'
    }
  ] : [];

  const topSummary = [
    {
      label: 'Órdenes visibles',
      value: ordenes.length || 0
    },
    {
      label: 'Clientes con horas',
      value: horas?.clientes?.length || 0
    },
    {
      label: 'Técnicos con extras',
      value: horasTecnicos?.tecnicos?.length || 0
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {error && (
        <div className="rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-700 shadow-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="relative overflow-hidden rounded-[30px] border border-white/80 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(34,211,238,0.14),_transparent_28%),linear-gradient(180deg,_rgba(255,255,255,0.96),_rgba(248,250,252,0.98))] p-6 shadow-[0_28px_60px_-30px_rgba(15,23,42,0.28)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200/80 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700 shadow-sm backdrop-blur-sm">
              <Activity className="h-3.5 w-3.5" />
              Pulso operativo
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 md:text-[2.1rem]">
              Dashboard administrador.
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600 md:text-[15px]">
              Señales clave, lectura rápida y foco en lo importante.
            </p>
          </div>

          <button
            onClick={() => {
              setLoading(true);
              cargarDatos();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 border-t border-slate-200/70 pt-5 sm:grid-cols-3">
          {topSummary.map((item) => (
            <div key={item.label} className="rounded-2xl border border-white/80 bg-white/70 px-4 py-3 backdrop-blur-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{item.label}</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{item.value}</p>
            </div>
          ))}
        </div>
      </section>
      
      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => (
            <DashboardStatCard
              key={card.label}
              icon={card.icon}
              label={card.label}
              value={card.value}
              hint={card.hint}
              detail={card.detail}
              accent={card.accent}
            />
          ))}
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-100 p-6 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2 text-gray-900">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Órdenes Recientes
              </h2>
              <p className="text-gray-500 text-sm mt-1">Últimas órdenes de trabajo procesadas</p>
            </div>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('cambiarSeccion', { detail: 'ordenes' }))}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1 hover:underline"
            >
              Ver todas <TrendingUp className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        <div className="p-0">
          {ordenes.length > 0 ? (
            <SmartAdaptiveList
              items={ordenes}
              onItemClick={(orden) => setOrdenDetalle(orden)}
              headers={['Cliente', 'Técnico', 'Estado', 'Fecha']}
              renderDesktopRow={(orden) => (
                <>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center border border-emerald-100">
                          <Users className="h-5 w-5 text-emerald-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {orden.nombre_cliente || orden.cliente_nombre || 'Sin cliente'}
                        </div>
                        {orden.sucursal && (
                          <div className="text-xs text-gray-500">{orden.sucursal}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700 font-medium">
                      {obtenerNombreTecnico(orden) || <span className="text-gray-400 italic">Sin asignar</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border ${
                      orden.estado === 'completada' || orden.estado === 'completado'
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : orden.estado === 'en_proceso' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : orden.estado === 'borrador'
                        ? 'bg-gray-50 text-gray-700 border-gray-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {(orden.estado === 'completada' || orden.estado === 'completado') && <CheckCircle className="w-3 h-3" />}
                      {orden.estado === 'en_proceso' && <Clock className="w-3 h-3" />}
                      {orden.estado === 'borrador' && <AlertCircle className="w-3 h-3" />}
                      {orden.estado !== 'completada' && orden.estado !== 'completado' && orden.estado !== 'en_proceso' && orden.estado !== 'borrador' && <AlertCircle className="w-3 h-3" />}
                      <span className="capitalize">{orden.estado?.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fmt(orden.fecha_inicio || orden.fecha)}
                  </td>
                </>
              )}
              renderMobileCard={(orden) => (
                <div className="space-y-3 p-1">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                        <Users className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-semibold text-gray-900">
                          {orden.nombre_cliente || orden.cliente_nombre || 'Sin cliente'}
                        </div>
                        {orden.sucursal && (
                          <div className="text-xs text-gray-500">{orden.sucursal}</div>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full border ${
                      orden.estado === 'completada' || orden.estado === 'completado'
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : orden.estado === 'en_proceso' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : orden.estado === 'borrador'
                        ? 'bg-gray-50 text-gray-700 border-gray-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {orden.estado}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm border-t border-gray-100 pt-3 mt-2">
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Técnico</span>
                      <span className="text-gray-900 font-medium">{obtenerNombreTecnico(orden) || 'Sin asignar'}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 block mb-1">Fecha</span>
                      <span className="text-gray-900">{fmt(orden.fecha_inicio || orden.fecha)}</span>
                    </div>
                  </div>
                </div>
              )}
            />
          ) : (
            <div className="text-center py-16 bg-white">
              <div className="bg-gray-50 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No hay órdenes recientes</h3>
              <p className="text-gray-500">Las órdenes aparecerán aquí cuando sean creadas</p>
            </div>
          )}
        </div>
      </div>

      {/* Horas Control */}
      {horas && (
        <section className="overflow-hidden rounded-[30px] border border-white/80 bg-gradient-to-br from-white via-slate-50 to-emerald-50/25 shadow-[0_22px_50px_-30px_rgba(15,23,42,0.25)]">
          <div className="border-b border-slate-200/70 px-6 py-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  <Clock className="w-3.5 h-3.5" />
                  Seguimiento mensual
                </div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-950">
                  Control de horas mensuales
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Comparación simple entre horas contratadas, trabajadas y restantes.
                </p>
              </div>
              <button
                onClick={() => setShowHistorial(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-50"
              >
                <Calendar className="w-4 h-4" />
                Ver historial
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6">
            {/* Stats de horas */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mb-8">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/80 p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-emerald-700">{formatearHoras(horas?.total_contratadas ?? 0)}</div>
                <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mt-1">Contratadas</div>
              </div>
              <div className="rounded-2xl border border-green-100 bg-green-50/80 p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-green-700">{formatearHoras(horas?.total_trabajadas ?? 0)}</div>
                <div className="text-xs font-medium text-green-600 uppercase tracking-wide mt-1">Trabajadas</div>
              </div>
              <div className="rounded-2xl border border-orange-100 bg-orange-50/80 p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-orange-700">{formatearHoras(horas?.total_restantes ?? 0)}</div>
                <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mt-1">Restantes</div>
              </div>
              <div className="rounded-2xl border border-red-100 bg-red-50/80 p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-red-700">+{formatearHoras(horas?.total_extras ?? 0)}</div>
                <div className="text-xs font-medium text-red-600 uppercase tracking-wide mt-1">Extras</div>
              </div>
            </div>
            
            {/* Tabla de clientes */}
            {horas.clientes?.length > 0 ? (
              <div className="overflow-hidden rounded-[24px] border border-slate-200/70 bg-white/80 shadow-sm">
                <SmartAdaptiveList
                  items={horas.clientes}
                  headers={['Cliente', 'Contratadas', 'Trabajadas', 'Restantes', 'Extras', 'Progreso']}
                  renderDesktopRow={(c) => (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                              {c.nombre_cliente?.substring(0, 2).toUpperCase() || 'CL'}
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {c.nombre_cliente || 'Cliente sin nombre'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                        {c.horas_contratadas ? formatearHoras(c.horas_contratadas) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {c.horas_trabajadas ? formatearHoras(c.horas_trabajadas) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {c.horas_restantes ? formatearHoras(c.horas_restantes) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-bold ${
                          c.horas_extras > 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {c.horas_extras > 0 ? `+${formatearHoras(c.horas_extras)}` : '0:00'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-100 rounded-full h-2.5 min-w-[100px] overflow-hidden">
                            <div 
                              className={`h-2.5 rounded-full transition-all duration-500 ${
                                c.porcentaje_uso > 100 
                                  ? 'bg-red-500' 
                                  : c.porcentaje_uso > 80 
                                  ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(c.porcentaje_uso, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-gray-700 min-w-[40px] text-right">
                            {c.porcentaje_uso ?? '-'}%
                          </span>
                        </div>
                      </td>
                    </>
                  )}
                  renderMobileCard={(c) => (
                    <div className="space-y-3 p-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 text-emerald-700 font-bold text-xs">
                            {c.nombre_cliente?.substring(0, 2).toUpperCase() || 'CL'}
                          </div>
                          <div className="ml-3 font-medium text-gray-900">
                            {c.nombre_cliente || 'Cliente sin nombre'}
                          </div>
                        </div>
                        <span className={`text-sm font-bold ${
                          c.porcentaje_uso > 100 ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {c.porcentaje_uso ?? '-'}%
                        </span>
                      </div>

                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            c.porcentaje_uso > 100 
                              ? 'bg-red-500' 
                              : c.porcentaje_uso > 80 
                              ? 'bg-yellow-500' 
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(c.porcentaje_uso, 100)}%` }}
                        />
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="bg-gray-50 p-2 rounded-lg border border-gray-100">
                          <div className="text-gray-500 mb-1">Cont.</div>
                          <div className="font-bold text-gray-800">{c.horas_contratadas ? formatearHoras(c.horas_contratadas) : '-'}</div>
                        </div>
                        <div className="bg-green-50 p-2 rounded-lg border border-green-100">
                          <div className="text-green-600 mb-1">Trab.</div>
                          <div className="font-bold text-green-700">{c.horas_trabajadas ? formatearHoras(c.horas_trabajadas) : '-'}</div>
                        </div>
                        <div className="bg-emerald-50 p-2 rounded-lg border border-emerald-100">
                          <div className="text-emerald-600 mb-1">Rest.</div>
                          <div className="font-bold text-emerald-700">{c.horas_restantes ? formatearHoras(c.horas_restantes) : '-'}</div>
                        </div>
                        <div className={`p-2 rounded-lg border ${c.horas_extras > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                          <div className={`${c.horas_extras > 0 ? 'text-red-600' : 'text-gray-500'} mb-1`}>Extra</div>
                          <div className={`font-bold ${c.horas_extras > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                            {c.horas_extras > 0 ? `+${formatearHoras(c.horas_extras)}` : '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>
            ) : (
              <div className="py-16 text-center bg-white/60 rounded-[24px] border border-slate-200/70">
                <div className="bg-slate-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Sin datos de horas</h3>
                <p className="text-gray-500">Los datos de control de horas aparecerán aquí</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Horas Extras Técnicos */}
      {horasTecnicos && (
        <section className="overflow-hidden rounded-[30px] border border-white/80 bg-gradient-to-br from-white via-slate-50 to-rose-50/25 shadow-[0_22px_50px_-30px_rgba(15,23,42,0.25)]">
          <div className="border-b border-slate-200/70 px-6 py-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-rose-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-700">
                <Clock className="w-3.5 h-3.5" />
                Resumen técnico
              </div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-950">
                Horas extras técnicos ({horasTecnicos.mes_actual})
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">Horas fuera de horario con lectura rápida y directa.</p>
            </div>
            
            <div className="flex gap-2">
              <select 
                value={filtroTecnicos.mes}
                onChange={(e) => setFiltroTecnicos({...filtroTecnicos, mes: parseInt(e.target.value)})}
                className="rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              >
                {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>
                    {new Date(2000, m-1, 1).toLocaleString('es-ES', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
                  </option>
                ))}
              </select>
              
              <select
                value={filtroTecnicos.anio}
                onChange={(e) => setFiltroTecnicos({...filtroTecnicos, anio: parseInt(e.target.value)})}
                className="rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              >
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="px-6 py-6 overflow-x-auto">
            {horasTecnicos.tecnicos && horasTecnicos.tecnicos.length > 0 ? (
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Técnico
                    </th>
                    <th scope="col" className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Órdenes
                    </th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Total Horas Extras
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {horasTecnicos.tecnicos.map((tecnico) => (
                    <tr key={tecnico.tecnico_id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 flex items-center justify-center text-red-700 font-bold text-sm shadow-sm">
                              {tecnico.nombre?.substring(0, 2).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">
                              {tecnico.nombre}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {tecnico.tecnico_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button 
                          onClick={() => setTechOrdenesModal(tecnico)}
                          className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors cursor-pointer border border-emerald-100 group"
                        >
                          {tecnico.ordenes_con_extras}
                          <Eye className="w-3 h-3 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-red-50 text-red-700 border border-red-100 shadow-sm">
                          +{formatearHoras(tecnico.minutos_extras / 60)} h
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-16 text-center bg-white/60 rounded-[24px] border border-slate-200/70">
                <div className="bg-slate-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4 border border-slate-100">
                  <Clock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Sin horas extras</h3>
                <p className="text-gray-500">No se registraron horas extras en este período</p>
              </div>
            )}
          </div>
        </section>
      )}

      {ordenDetalle && (
        <OrdenDetalleModal
          orden={ordenDetalle}
          onClose={() => setOrdenDetalle(null)}
          onUpdate={cargarDatos}
          resolverNombreTecnico={obtenerNombreTecnico}
        />
      )}

      {techOrdenesModal && (
        <OrdenesExtrasModal
          tecnico={techOrdenesModal}
          onClose={() => setTechOrdenesModal(null)}
          onVerOrden={(orden) => {
            // Buscar la orden completa en el estado si es posible, o cargarla
            // Por simplicidad, cerramos este modal y abrimos el detalle si tenemos ID
            // Pero necesitamos el objeto completo para OrdenDetalleModal.
            // Una opción rápida es hacer fetch de la orden individual.
            setLoading(true);
            http.get(`/ordenes/${orden.id}`)
              .then(res => {
                setTechOrdenesModal(null);
                setOrdenDetalle(res.data);
              })
              .catch(err => console.error("Error cargando orden", err))
              .finally(() => setLoading(false));
          }}
        />
      )}

      {showHistorial && (
        <HistorialHorasModal onClose={() => setShowHistorial(false)} />
      )}
    </div>
  );
}